/**
 * ReferenceCatalogDrawer — generic правый drawer 560px для редактора
 * reference-сущности (AK-231 Pilot). Не знает про культуру/болезнь/etc:
 * принимает title (`«Создание» / «Редактирование» + entityLabel`), body
 * (секции от конкретной страницы) и footer-callback'и.
 *
 * Внутрь рендерим `<EditorSection>` для каждой секции (нумерованный header).
 * 5 секций видны сразу (не accordion) — соответствует low-fi wireframe AK-231
 * (founder approved 2026-06-21).
 */
import { type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

export interface ReferenceCatalogDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** «Редактирование культуры» / «Создание культуры» / … */
  mode: 'create' | 'edit';
  /** Лейбл сущности в title и хедере (родительный падеж: «культуры», «болезни»). */
  entityLabel: string;
  /** Дополнительный preview под заголовком — color-chip + название. */
  headerSlot?: ReactNode;
  /** Доп. действия в правой части шапки (например, «Создать на основе»). */
  headerActionSlot?: ReactNode;
  /** Сами секции редактора. */
  children: ReactNode;
  /** Disabled = есть validation errors. */
  saveDisabled?: boolean;
  saveLoading?: boolean;
  onSave: () => void;
  onCancel?: () => void;
}

export function ReferenceCatalogDrawer({
  open,
  onOpenChange,
  mode,
  entityLabel,
  headerSlot,
  headerActionSlot,
  children,
  saveDisabled,
  saveLoading,
  onSave,
  onCancel,
}: ReferenceCatalogDrawerProps) {
  const titleText = mode === 'create' ? `Создание ${entityLabel}` : `Редактирование ${entityLabel}`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[560px]"
      >
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between border-b border-border px-6 py-4">
          <div className="min-w-0 flex-1 pr-8">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {mode === 'create' ? 'Создание' : 'Редактирование'}
            </div>
            <SheetTitle className="mt-0.5">{titleText}</SheetTitle>
            {headerSlot && (
              <SheetDescription asChild>
                <div className="mt-1.5">{headerSlot}</div>
              </SheetDescription>
            )}
          </div>
          {headerActionSlot && (
            <div className="ml-2 mr-8 flex shrink-0 items-center gap-2">
              {headerActionSlot}
            </div>
          )}
        </div>

        {/* Body — scrollable секции */}
        <div className="flex-1 overflow-y-auto px-6 pb-4">{children}</div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border bg-secondary/40 px-6 py-3">
          <Button
            variant="outline"
            onClick={() => {
              onCancel?.();
              onOpenChange(false);
            }}
            disabled={saveLoading}
          >
            Отмена
          </Button>
          <Button onClick={onSave} disabled={saveDisabled || saveLoading}>
            <Check className="h-4 w-4" />
            {saveLoading ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export interface EditorSectionProps {
  number: number;
  title: string;
  hint?: string;
  children: ReactNode;
  className?: string;
}

/**
 * EditorSection — нумерованная секция формы в drawer'е. Подходит для
 * всех справочников, разделённых на разделы (Идентификация / Визуал / …).
 */
export function EditorSection({
  number,
  title,
  hint,
  children,
  className,
}: EditorSectionProps) {
  return (
    <section
      className={cn(
        'border-t border-border py-5 first:border-t-0 first:pt-4',
        className,
      )}
    >
      <header className="mb-3 flex items-center gap-2">
        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-secondary text-[11px] font-bold text-muted-foreground">
          {number}
        </span>
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {hint && (
          <span className="text-xs font-normal text-muted-foreground">· {hint}</span>
        )}
      </header>
      <div>{children}</div>
    </section>
  );
}
